import { TableColumn } from "react-data-table-component";
import { GBP_SYMBOL } from ".";
import { formatCurrency, formatRoomNumber, toTitleCase } from "lib/utils/util";
import {
  formatDate,
  formatDateUTC,
  toFormattedDate,
  toFormattedDateUTC,
  formatDateUTCwithoutDay,
} from "lib/utils/date-utils";


export const CLINIC_WORKING_HOURS_COLUMNS: TableColumn<WorkingHour>[] = [
  {
    name: "Day",
    selector: (row: WorkingHour) => row.day,
    sortable: true,
    width: "auto",
  },
  {
    name: "From",
    selector: (row: WorkingHour) => row.fromTime,
    sortable: true,
    width: "30%",
  },
  {
    name: "To",
    selector: (row: WorkingHour) => row.toTime,
    sortable: true,
    width: "20%",
    center: true,
  },
];

export const PRACTITIONERS_COLUMNS: TableColumn<PractitionerDetails>[] = [
  {
    name: "Name",
    selector: (row: PractitionerDetails) => `${row.firstName} ${row.lastName}`,
    sortable: true,
    width: "auto",
  },
  {
    name: "Email",
    selector: (row: PractitionerDetails) => row.email,
    sortable: true,
    width: "30%",
  },
  {
    name: "Active",
    selector: (row: PractitionerDetails) => (row.isActive ? "Yes" : "No"),
    sortable: true,
    width: "20%",
    center: true,
  },

  {
    name: "Disabled",
    selector: (row: PractitionerDetails) => (row.isDisabled ? "Yes" : "No"),
    sortable: true,
    width: "20%",
    center: true,
  },
];

export const SUBSCRIPTIONS_COLUMNS: TableColumn<SubscriptionDetails>[] = [
  {
    name: "Title",
    selector: (row: SubscriptionDetails) => row.title,
    sortable: true,
    width: "20%",
  },
  {
    name: "Sub-Title",
    selector: (row: SubscriptionDetails) => row.subTitle,
    sortable: true,
    width: "30%",
  },
  {
    name: "SKU",
    selector: (row: SubscriptionDetails) => row.sku,
    sortable: true,
    width: "15%",
  },
  {
    name: "P.P.D.",
    selector: (row: SubscriptionDetails) => `${GBP_SYMBOL} ${row.perDayPrice}`,
    sortable: true,
    width: "15%",
    center: true,
  },
  {
    name: "Clinic",
    selector: (row: SubscriptionDetails) => row.clinicName ?? "",
    sortable: true,
    width: "20%",
    center: true,
  },
];

export const APPOINTMENTS_COLUMNS: TableColumn<Appointment>[] = [
  {
    name: "Date",
    selector: (row: Appointment) => row.date,
    sortable: true,
    width: "auto",
  },
  {
    name: "Time",
    selector: (row: Appointment) => row.time,
    sortable: true,
    width: "10%",
  },
  {
    name: "Clinic",
    selector: (row: Appointment) => row.clinicName,
    sortable: true,
    width: "20%",
  },
  {
    name: "Practitioner",
    selector: (row: Appointment) => row.practitionerName,
    sortable: true,
    width: "20%",
  },
  {
    name: "Patient",
    selector: (row: Appointment) => row.patientName,
    sortable: true,
    width: "20%",
  },

  {
    name: "Status",
    selector: (row: Appointment) => row.status,
    sortable: true,
    width: "20%",
  },
];

export const USER_COLUMNS: TableColumn<ClinicUser>[] = [
  {
    name: "First Name",
    selector: (row) => row.firstName,
    sortable: true,
    width: "auto",
  },
  {
    name: "Last Name",
    selector: (row) => row.lastName,
    sortable: true,
    width: "auto",
  },
  {
    name: "Email",
    selector: (row) => row.email,
    sortable: true,
    width: "auto",
  },
  {
    name: "Role",
    selector: (row) => toTitleCase(row.role),
    sortable: true,
    width: "auto",
  },
];

export const THERAPIES_COLUMNS: TableColumn<Therapy>[] = [
  {
    name: "Sr. No.",
    selector: (row: Therapy, index: number) => row.index ?? index + 1,
    sortable: true,
    width: "10%",
    center: true,
  },
  {
    name: "Therapy",
    selector: (row: Therapy) => row.name,
    sortable: true,
    width: "20%",
    wrap: true,
  },
  {
    name: "Description",
    selector: (row: Therapy) => row.description,
    width: "40%",
    hide: 600,
    wrap: true,
  },
  {
    name: "Medical",
    selector: (row: Therapy) => (row.isMedical ? "Yes" : "No"),
    center: true,
    width: "10%",
    hide: 600,
  },
  {
    name: "Website",
    selector: (row: Therapy) => (row.isShowOnWebsite ? "Visible" : "Hidden"),
    center: true,
    width: "10%",
    hide: 600,
  },
  {
    name: "Session",
    selector: (row: Therapy) => (row.isSessionBookingEnabled ? "Yes" : "No"),
    center: true,
    width: "10%",
    hide: 600,
  },
];

export const PACKAGES_COLUMNS: TableColumn<Package>[] = [
  {
    name: "Sr. No.",
    selector: (row: Package, index: number) => row.index ?? index + 1,
    sortable: true,
    width: "10%",
    center: true,
  },
  {
    name: "Package Name",
    selector: (row: Package) => row.name,
    sortable: true,
    width: "20%",
    wrap: true,
  },
  {
    name: "Description",
    selector: (row: Package) => row.description,
    width: "30%",
    hide: 600,
    wrap: true,
  },
  {
    name: "Credits",
    selector: (row: Package) => row.credits,
    center: true,
    width: "10%",
    hide: 600,
  },
  {
    name: "Price",
    selector: (row: Package) => `£${row.price}`,
    center: true,
    width: "10%",
    hide: 600,
  },
  {
    name: "Validity",
    selector: (row: Package) => row.validity ? `${row.validity} Months` : "No expiry",
    center: true,
    width: "15%",
    hide: 600,
  },
];

export const FEATURES_COLUMNS: TableColumn<FeatureDetails>[] = [
  {
    name: "Sr. No.",
    selector: (row: FeatureDetails, index: number) => index + 1,
    width: "15%",
    center: true,
  },
  {
    name: "Feature",
    selector: (row: FeatureDetails) => row.label,
    width: "60%",
    wrap: true,
  },
  {
    name: "Available",
    selector: (row: FeatureDetails) => (row.isAvailable ? "Yes" : "No"),
    width: "auto",
    center: true,
  },
];

export const PRACTITIONER_THERAPIES_COLUMNS: TableColumn<PractitionerTherapy>[] =
  [
    {
      name: "Name",
      selector: (row: PractitionerTherapy, index: number) => row.name,
      sortable: true,
      width: "auto",
      wrap: true,
    },
    {
      name: "Session Time",
      selector: (row: PractitionerTherapy) => row.sessionTime,
      sortable: true,
      width: "30%",
      center: true,
    },
    {
      name: "Session Price",
      selector: (row: PractitionerTherapy) => row.perSessionPrice,
      width: "25%",
      hide: 600,
      wrap: true,
      right: true,
    },
  ];

export const PRACTITIONER_SUBSCRIPTIONS_COLUMNS1: TableColumn<PractitionerSubscriptionDetails>[] =
  [
    {
      name: "Title",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatRoomNumber(row.room),
      sortable: true,
      width: "10%",
      wrap: true,
    },
    {
      name: "Description",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatRoomNumber(row.room),
      sortable: true,
      width: "45%",
      wrap: true,
    },
    {
      name: "Clinic",
      selector: (row: PractitionerSubscriptionDetails) => row.clinicName,
      width: "15%",
      hide: 600,
      wrap: true,
    },
    {
      name: "Active",
      selector: (row: PractitionerSubscriptionDetails) =>
        row.isActive ? "Yes" : "No",
      width: "10%",
      hide: 600,
      wrap: true,
      center: true,
    },
    {
      name: "Expires On",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatDate(row.toDate),
      width: "20%",
      hide: 600,
      wrap: true,
      center: true,
    },
  ];

export const PRACTITIONER_SUBSCRIPTIONS_COMPACT_COLUMNS: TableColumn<PractitionerSubscriptionDetails>[] =
  [
    {
      name: "Title",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatRoomNumber(row.room),
      sortable: true,
      width: "30%",
      wrap: true,
    },
    {
      name: "Clinic",
      selector: (row: PractitionerSubscriptionDetails) => row.clinicName,
      width: "40%",
      hide: 600,
      wrap: true,
    },
    {
      name: "Expires On",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatDate(row.toDate),
      width: "30%",
      hide: 600,
      wrap: true,
      center: true,
    },
  ];

export const PRACTITIONER_ROOM_RESERVATIONS_COLUMNS: TableColumn<RoomReservation>[] =
  [
    {
      name: "Room #",
      selector: (row: RoomReservation) => `Room ${row.room}`,
      width: "30%",
      sortable: true,
      center: true,
    },
    {
      name: "Date",
      selector: (row: RoomReservation) => formatDateUTC(row.date),
      sortable: true,
      width: "30%",
      wrap: true,
    },
    {
      name: "Clinic",
      selector: (row: RoomReservation) => row.clinicName,
      width: "40%",
      sortable: true,
      wrap: true,
    },
  ];

export const PATIENT_BOOKINGS_COLUMN: TableColumn<AppointmentBookingDetails>[] =
  [
    {
      name: "Date",
      selector: (row: AppointmentBookingDetails) =>
        formatDateUTC(new Date(row.fromTime)),
      width: "15%",
      sortable: true,
      center: true,
    },
    {
      name: "Time",
      selector: (row: AppointmentBookingDetails) =>
        toFormattedDate(new Date(row.fromTime), "hh:mm a"),
      width: "15%",
      sortable: true,
      center: true,
    },
    {
      name: "Duration",
      selector: (row: AppointmentBookingDetails) => `${row.duration} mins`,
      width: "15%",
      sortable: true,
      center: true,
    },
    {
      name: "Clinic",
      selector: (row: AppointmentBookingDetails) => row.clinicName,
      width: "15%",
      sortable: true,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row: AppointmentBookingDetails) => row.status,
      width: "15%",
      sortable: true,
      wrap: true,
    },
    {
      name: "Patient",
      selector: (row: AppointmentBookingDetails) =>
        row.patientName ?? "Marked Busy",
      width: "25%",
      sortable: true,
      wrap: true,
    },
  ];

export const CLINIC_ROOM_COLUMNS: TableColumn<RoomDetails>[] = [
  {
    name: "Room #",
    selector: (row: RoomDetails) => `Room ${row.roomNumber}`,
    sortable: true,
    width: "40%",
  },
  {
    name: "Price/Day",
    selector: (row: RoomDetails) => `${GBP_SYMBOL} ${row.pricePerDay}`,
    sortable: true,
    width: "40%",
    right: true,
  },
  {
    name: "Active",
    selector: (row: RoomDetails) => (row.isActive ? "Yes" : "No"),
    sortable: true,
    width: "20%",
    center: true,
  },
];

export const PRACTITIONER_SUBSCRIPTIONS_COLUMNS: TableColumn<PractitionerSubscriptionDetails>[] =
  [
    {
      name: "Clinic",
      selector: (row: PractitionerSubscriptionDetails) => row.clinicName,
      sortable: true,
      width: "20%",
    },
    {
      name: "Room",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatRoomNumber(row.room),
      sortable: true,
      width: "10%",
    },
    {
      name: "Days",
      selector: (row: PractitionerSubscriptionDetails) => `${row.dates.length}`,
      center: true,
      width: "10%",
    },

    {
      name: "Room Cost",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatCurrency(row.perDayPrice),
      right: true,
      width: "10%",
    },
    {
      name: "Total Cost",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatCurrency(row.totalCost),
      right: true,
      width: "15%",
    },
    {
      name: "Paid",
      selector: (row: PractitionerSubscriptionDetails) =>
        `${row.isActive ? "Yes" : "No"}`,
      center: true,
      width: "10%",
    },
    {
      name: "Cancelled",
      selector: (row: PractitionerSubscriptionDetails) =>
        `${row.isCancelled ? "Yes" : "No"}`,
      center: true,
      width: "15%",
    },
    {
      name: "Booking Id",
      selector: (row: any) => row.bookingId,
      center: true,
      width: "10%",
    }
  ];

export const PRACTITIONER_SUBSCRIPTIONS_WITH_NAME_COLUMNS: TableColumn<PractitionerSubscriptionDetails>[] =
  [
    {
      name: "Clinic",
      selector: (row: PractitionerSubscriptionDetails) => row.clinicName,
      sortable: true,
      wrap: true,
      width: "15%",
    },
    {
      name: "Practitioner",
      selector: (row: PractitionerSubscriptionDetails) => row.practitionerName,
      sortable: true,
      wrap: true,
      width: "15%",
    },
    {
      name: "Room",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatRoomNumber(row.room),
      sortable: true,
      wrap: true,
      width: "10%",
    },
    {
      name: "Days",
      selector: (row: PractitionerSubscriptionDetails) => `${row.dates.length}`,
      center: true,
      wrap: true,
      width: "10%",
    },

    {
      name: "Room Cost",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatCurrency(row.perDayPrice),
      right: true,
      wrap: true,
      width: "15%",
    },
    {
      name: "Total Cost",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatCurrency(row.totalCost),
      right: true,
      width: "10%",
    },
    {
      name: "Paid",
      selector: (row: PractitionerSubscriptionDetails) =>
        `${row.isActive ? "Yes" : "No"}`,
      center: true,
      width: "10%",
    },
    {
      name: "Cancelled",
      selector: (row: PractitionerSubscriptionDetails) =>
        `${row.isCancelled ? "Yes" : "No"}`,
      center: true,
      width: "15%",
    },
  ];

export const PAITENT_APPOINTMENT_BOOKING_DETAILS_COLUMNS: TableColumn<PatientAppointmentBookingDetails>[] =
  [
    {
      name: "Clinic",
      selector: (row: PatientAppointmentBookingDetails) => row.clinicName,
      right: false,
      center: false,
      width: "auto",
    },
    {
      name: "Practitioner",
      cell: (row: PatientAppointmentBookingDetails) => row.practitionerName,
      center: true,
      width: "auto",
    },
    {
      name: "Date",
      selector: (row: PatientAppointmentBookingDetails) =>
        formatDate(row.fromTime),
      center: true,
      width: "auto",
    },
    {
      name: "From Time",
      selector: (row: PatientAppointmentBookingDetails) =>
        toFormattedDate(new Date(row.fromTime), "hh:mm a"),
      center: true,
      width: "auto",
    },
    {
      name: "Duration",
      selector: (row: PatientAppointmentBookingDetails) => row.duration,
      center: true,
      width: "auto",
    },
    {
      name: "Total Cost",
      selector: (row: PatientAppointmentBookingDetails) => row.totalCost,
      right: true,
      width: "auto",
    },
  ];
export const PRACTITIONER_INCOME_COLUMNS: TableColumn<PatientAppointmentBookingDetails>[] =
  [
    {
      name: "Clinic",
      selector: (row: PatientAppointmentBookingDetails) => row.clinicName,
      right: false,
      center: false,
      width: "20%",
    },
    {
      name: "Patient",
      cell: (row: PatientAppointmentBookingDetails) => row.patientName,
      center: true,
      width: "20%",
    },
    {
      name: "Date",
      selector: (row: PatientAppointmentBookingDetails) =>
        formatDate(row.fromTime),
      center: true,
      width: "20%",
    },

    {
      name: "Total Cost",
      selector: (row: PatientAppointmentBookingDetails) => row.totalCost,
      right: true,
      width: "20%",
    },
    {
      name: "Invoice",
      cell: (row: PatientAppointmentBookingDetails) => (
        <a
          href={row.invoiceURL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          View
        </a>
      ),
      center: true,
      width: "20%",
    },
  ];

export const PRACTITIONER_EXPENSES_COLUMNS: TableColumn<PractitionerSubscriptionDetails>[] =
  [
    {
      name: "Room",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatRoomNumber(row.room),
      sortable: true,
      width: "20%",
    },
    {
      name: "Days",
      selector: (row: PractitionerSubscriptionDetails) => `${row.dates.length}`,
      center: true,
      width: "20%",
    },

    {
      name: "Room Cost",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatCurrency(row.perDayPrice),
      right: true,
      width: "20%",
    },
    {
      name: "Income",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatCurrency(row.totalCost),
      right: true,
      width: "20%",
    },
    {
      name: "Invoice",
      cell: (row: PractitionerSubscriptionDetails) => (
        <a
          href={row.invoiceURL ?? "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          View
        </a>
      ),
      center: true,
      width: "20%",
    },
  ];

export const CLINIC_PATIENTS_INCOME_COLUMNS: TableColumn<PatientAppointmentBookingDetails>[] =
  [
    {
      name: "Patient",
      cell: (row: PatientAppointmentBookingDetails) => row.patientName,
      center: true,
      width: "30%",
    },
    {
      name: "Date",
      selector: (row: PatientAppointmentBookingDetails) =>
        formatDate(row.fromTime),
      center: true,
      width: "30%",
    },

    {
      name: "Income",
      selector: (row: PatientAppointmentBookingDetails) => row.totalCost,
      right: true,
      width: "20%",
    },
    {
      name: "Invoice",
      cell: (row: PatientAppointmentBookingDetails) => (
        <a
          href={row.invoiceURL}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          View
        </a>
      ),
      center: true,
      width: "20%",
    },
  ];

export const CLINIC_PRACTITIONERS_INCOME_COLUMNS: TableColumn<PractitionerSubscriptionDetails>[] =
  [
    {
      name: "Room",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatRoomNumber(row.room),
      sortable: true,
      width: "30%",
    },
    {
      name: "Days",
      selector: (row: PractitionerSubscriptionDetails) => `${row.dates.length}`,
      center: true,
      width: "20%",
    },
    {
      name: "Income",
      selector: (row: PractitionerSubscriptionDetails) =>
        formatCurrency(row.totalCost),
      right: true,
      width: "30%",
    },
    {
      name: "Invoice",
      cell: (row: PractitionerSubscriptionDetails) => (
        <a
          href={row.invoiceURL ?? "#"}
          target="_blank"
          rel="noopener noreferrer"
          className="underline"
        >
          View
        </a>
      ),
      center: true,
      width: "20%",
    },
  ];
export const PAITENT_SESSION_BOOKING_DETAILS_COLUMNS: TableColumn<SessionBookingDetails>[] =
  [
    {
      name: "Clinic",
      selector: (row: SessionBookingDetails) => row.clinicName,
      right: false,
      center: false,
      width: "auto",
    },
    {
      name: "Therapy",
      selector: (row: SessionBookingDetails) => row.therapyName,
      right: false,
      center: true,
      width: "auto",
    },
    {
      id: "date",
      name: "Date",
      selector: (row: SessionBookingDetails) =>
        formatDateUTC(row.fromTime),
      center: true,
      width: "auto",
      sortable: true,
    },
    {
      name: "From Time",
      selector: (row: SessionBookingDetails) =>
        toFormattedDateUTC(new Date(row.fromTime), "hh:mm a"),
      center: true,
      width: "auto",
    },
    {
      name: "Duration",
      selector: (row: SessionBookingDetails) => row.duration,
      center: true,
      width: "auto",
    },
    {
      name: "Status",
      selector: (row: SessionBookingDetails) => row.status,
      center: true,
      width: "auto",
    },
    {
      name: "Total Cost",
      selector: (row: SessionBookingDetails) => row.totalCost,
      right: true,
      width: "auto",
    },
];

export const ALL_SESSION_BOOKING_DETAILS_COLUMNS: TableColumn<SessionBookingDetails>[] =
  [
    {
      name: "Clinic",
      selector: (row: SessionBookingDetails) => row.clinicName,
      right: false,
      center: false,
      width: "auto",
    },
    {
      name: "Patient",
      selector: (row: SessionBookingDetails) => row.patientName,
      right: false,
      center: true,
      width: "auto",
    },
    {
      name: "Therapy",
      selector: (row: SessionBookingDetails) => row.therapyName,
      right: false,
      center: true,
      width: "auto",
    },
    {
      id: "date",
      name: "Date",
      selector: (row: SessionBookingDetails) =>
        formatDateUTCwithoutDay(row.fromTime),
      center: true,
      width: "auto",
      sortable: true,
      sortFunction: (rowA, rowB) => {
        const a = new Date(rowA.fromTime).getTime();
        const b = new Date(rowB.fromTime).getTime();
        return a - b;
      },
    },
    {
      name: "From Time",
      selector: (row: SessionBookingDetails) =>
        toFormattedDateUTC(new Date(row.fromTime), "hh:mm a"),
      center: true,
      width: "auto",
    },
    {
      name: "Duration",
      selector: (row: SessionBookingDetails) => row.duration,
      center: true,
      width: "auto",
    },
    {
      name: "Total Cost",
      selector: (row: SessionBookingDetails) => row.totalCost,
      right: true,
      width: "auto",
    },
];

export const PATIENTS_COLUMNS: TableColumn<PatientsList>[] = [
  {
    name: "Name",
    selector: (row: PatientsList) => row.name,
    sortable: true,
    width: "20%",
  },
  {
    name: "Email",
    selector: (row: PatientsList) => row.email,
    sortable: true,
    width: "20%",
    center: true,
  },
  {
    name: "Phone",
    selector: (row: PatientsList) => row.phone,
    sortable: true,
    width: "15%",
    center: true,
  },
  {
    id: "date",
    name: "Join Date",
    selector: (row: PatientsList) => 
      formatDateUTCwithoutDay(new Date(row.join_date)),
    sortable: true,
    width: "15%",
    center: true,
    sortFunction: (rowA, rowB) => {
      const a = new Date(rowA.join_date).getTime();
      const b = new Date(rowB.join_date).getTime();
      return a - b;
    },
  },
  {
    name: "Disabled",
    selector: (row: PatientsList) => row.disabled ? "Yes" : "No",
    sortable: true,
    width: "10%",
    center: true,
  },
  {
    name: "Last Session Booking Date",
    selector: (row: PatientsList) => 
    row.last_session_booking_date
        ? formatDateUTCwithoutDay(new Date(row.last_session_booking_date))
        : "New Patient",
    sortable: true,
    width: "20%",
    right: true,
  },
  /* {
    name: "Post Code",
    selector: (row: Patient) => row.postcode,
    sortable: true,
    center: true,
  },
  {
    name: "Age",
    selector: (row: Patient) => row.age,
    sortable: true,
    right: true,
  }, */
];

export const MEMBERSHIP_PACKAGES_COLUMNS: TableColumn<MembershipPackage>[] = [
  {
    name: "Sr. No.",
    selector: (row: MembershipPackage, index: number) => row.index ?? index + 1,
    sortable: true,
    width: "10%",
    center: true,
  },
  {
    name: "Membership Package Name",
    selector: (row: MembershipPackage) => row.name,
    sortable: true,
    wrap: true,
  },
  {
    name: "Credits",
    selector: (row: MembershipPackage) => row.credits,
    center: true,
    width: "10%",
    hide: 600,
  },
  {
    name: "Price",
    selector: (row: MembershipPackage) => `£${row.price}`,
    center: true,
    width: "10%",
    hide: 600,
  },
  {
    name: "Price per Credits",
    selector: (row: MembershipPackage) => `£${row.pricePerCredit}`,
    center: true,
    width: "15%",
    hide: 600,
  },
  {
    name: "Discount",
    selector: (row: MembershipPackage) => `${row.discount}%`,
    center: true,
    width: "15%",
    hide: 600,
  },
];

export const PAITENT_MEMBERSHIP_PACKAGES_LIST_DETAILS_COLUMNS: TableColumn<BuyMembershipPackageList>[] = [
  {
    name: "Membership Package Name",
    selector: (row: BuyMembershipPackageList) => row.name,
    sortable: true,
    wrap: true,
  },
  {
    name: "Credits",
    selector: (row: BuyMembershipPackageList) => row.credits,
    center: true,
    hide: 600,
  },
  {
    name: "Price",
    selector: (row: BuyMembershipPackageList) => `£${row.price}`,
    center: true,
    hide: 600,
  },
  {
    name: "Status",
    selector: (row: BuyMembershipPackageList) => row.status,
    center: true,
    hide: 600,
  },
  {
    name: "Subscribe Date",
    selector: (row: BuyMembershipPackageList) => `${row.subscribeDate}`,
    center: true,
    hide: 600,
  },
  {
    name: "Next Invoice Date",
    selector: (row: BuyMembershipPackageList) => row.status === 'cancelled' ? '-' : `${row.nextInvoiceDate}`,
    center: true,
    hide: 600,
  },
];

export const PATIENT_CREDITS_LIST_COLUMNS = [
  { 
    name: "Package Name", 
    selector: (row: any) => row.packageName,
    sortable: true,
  },
  { 
    name: "Type",
    selector: (row: any) => row.type,
    sortable: true,
    center: true,
  },
  { 
    name: "Total Credits",
    selector: (row: any) => row.totalCredits,
    sortable: true,
    center: true,
  },
  { 
    name: "Credits Remaining",
    selector: (row: any) => row.creditsRemaining,
    sortable: true,
    center: true,
  },
  { 
    name: "Expiry Date",
    selector: (row: any) => row.expireDate, 
    sortable: true,
    center: true,
  },
];